<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'

defineProps<{
  text: string
  position?: 'top' | 'bottom' | 'left' | 'right'
}>()

const tooltipId = ref(`tooltip-${Math.random().toString(36).slice(2, 11)}`)
const isVisible = ref(false)
const wrapperRef = ref<HTMLElement | null>(null)

function showTooltip() {
  isVisible.value = true
}

function hideTooltip() {
  isVisible.value = false
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isVisible.value) {
    hideTooltip()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)

  // Add aria-describedby to the first focusable element inside the slot
  nextTick(() => {
    if (wrapperRef.value) {
      const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      const focusableElement = wrapperRef.value.querySelector(focusableSelector)
      if (focusableElement) {
        const existingDescribedBy = focusableElement.getAttribute('aria-describedby')
        if (existingDescribedBy) {
          focusableElement.setAttribute('aria-describedby', `${existingDescribedBy} ${tooltipId.value}`)
        } else {
          focusableElement.setAttribute('aria-describedby', tooltipId.value)
        }
      }
    }
  })
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <div
    ref="wrapperRef"
    class="tooltip-wrapper"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @focusin="showTooltip"
    @focusout="hideTooltip"
  >
    <slot />
    <span
      :id="tooltipId"
      class="tooltip-text"
      :class="[`tooltip-${position ?? 'top'}`, { 'tooltip-visible': isVisible }]"
      role="tooltip"
      :aria-hidden="!isVisible"
    >
      {{ text }}
    </span>
  </div>
</template>

<style scoped>
.tooltip-wrapper {
  position: relative;
  display: inline-flex;
}

.tooltip-text {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  z-index: 100;
  padding: 0.375rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
  background-color: var(--color-gray-900);
  border: 1px solid var(--color-gray-700);
  border-radius: 0.25rem;
  white-space: nowrap;
  pointer-events: none;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

/* Position variants */
.tooltip-top {
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
}

.tooltip-bottom {
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
}

.tooltip-left {
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
}

.tooltip-right {
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
}

/* Arrow indicators */
.tooltip-top::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: var(--color-gray-900) transparent transparent transparent;
}

.tooltip-bottom::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent var(--color-gray-900) transparent;
}

.tooltip-left::after {
  content: '';
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent transparent var(--color-gray-900);
}

.tooltip-right::after {
  content: '';
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: transparent var(--color-gray-900) transparent transparent;
}

/* Show tooltip when visible state is true */
.tooltip-visible {
  visibility: visible;
  opacity: 1;
}

/* Respect user motion preferences */
@media (prefers-reduced-motion: reduce) {
  .tooltip-text {
    transition: none;
  }
}
</style>
